'use client';

import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import Image from 'next/image';
import { ArrowLeft, ArrowRight, X } from '@phosphor-icons/react';
import {
  AlertCircleIcon,
  CheckCircleIcon,
  CheckIcon,
  PhoneIcon,
} from '@/components/icons';
import {
  AuthPhoneField,
  formatPhone,
  normalizePhone,
  isRussianMobile,
} from './auth-fields';
import { useAuthFlow } from './use-auth-flow';
import './auth-modal.css';

type AuthFlow = ReturnType<typeof useAuthFlow>;
const CHARACTER = '/storefront/faq-mascot-phone-v1.webp';

function AuthNotice({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <div className="auth-ui-notice" id={id} role="alert">
      <AlertCircleIcon size={20} />
      <p>{children}</p>
    </div>
  );
}

function AuthPrimary({
  children,
  busy,
  disabled,
  onClick,
  type = 'button',
}: {
  children: React.ReactNode;
  busy?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      className="auth-ui-primary"
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
    >
      <span>{children}</span>
      {busy ? (
        <span className="auth-ui-spinner" aria-hidden="true" />
      ) : (
        <ArrowRight size={21} aria-hidden="true" />
      )}
    </button>
  );
}

function PhoneStep({ flow }: { flow: AuthFlow }) {
  const [phone, setPhone] = useState(() => (flow.phone ? formatPhone(flow.phone) : '+7 ('));
  const normalized = normalizePhone(phone);
  const valid = isRussianMobile(normalized);
  const invalid =
    normalized.length === 11 && !valid
      ? 'Нужен российский мобильный номер: +7 9XX XXX-XX-XX'
      : undefined;
  return (
    <form
      className="auth-ui-phone-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && !flow.busy && !flow.retryAfter) void flow.requestCode(normalized);
      }}
    >
      <AuthPhoneField value={phone} onChange={setPhone} disabled={!!flow.busy} error={invalid} />
      <p className="auth-ui-field-hint">Подтвердим номер с помощью звонка.</p>
      {flow.error && flow.errorTarget === 'phone' && <AuthNotice>{flow.error}</AuthNotice>}
      <AuthPrimary type="submit" busy={!!flow.busy} disabled={!valid || flow.retryAfter > 0}>
        {flow.busy
          ? 'Запрашиваем вход…'
          : flow.retryAfter > 0
            ? `Повторить через ${flow.retryAfter} с`
            : 'Продолжить'}
      </AuthPrimary>
      <p className="auth-ui-consent">
        Продолжая, вы соглашаетесь с{' '}
        <a href="/privacy" target="_blank" rel="noopener noreferrer">
          политикой конфиденциальности
        </a>
        .
      </p>
    </form>
  );
}

function CallMeStep({ flow }: { flow: AuthFlow }) {
  const raw = flow.callmeNumber?.replace(/\D/g, '') || '';
  const pretty =
    raw.length === 11
      ? `+${raw[0]} ${raw.slice(1, 4)} ${raw.slice(4, 7)}-${raw.slice(7, 9)}-${raw.slice(9, 11)}`
      : flow.callmeNumber;
  const expired = flow.callStatus === 'expired';
  return (
    <div className="auth-ui-callme-step">
      {expired ? (
        <>
          <AuthNotice>Время ожидания истекло. Запросите вход ещё раз.</AuthNotice>
          <AuthPrimary onClick={flow.back}>Начать заново</AuthPrimary>
        </>
      ) : (
        <>
          <ol className="auth-ui-call-steps">
            <li>
              <span>1</span>
              <p>Позвоните со своего номера на номер ниже.</p>
            </li>
            <li>
              <span>2</span>
              <p>Звонок сбросится автоматически. Мы проверим номер и откроем аккаунт.</p>
            </li>
          </ol>
          <a className="auth-ui-call-number" href={`tel:+${raw}`}>
            <span>Номер для подтверждения</span>
            <strong>{pretty}</strong>
            <span className="auth-ui-call-link">
              <PhoneIcon size={18} weight="fill" />
              Позвонить для входа
              <ArrowRight size={18} />
            </span>
          </a>
          <div className="auth-ui-call-wait" role="status">
            <span className="auth-ui-spinner" aria-hidden="true" />
            <span>
              {flow.busy === 'profile'
                ? 'Открываем аккаунт…'
                : flow.callStatus === 'network'
                  ? 'Восстанавливаем связь…'
                  : 'Ждём ваш звонок'}
            </span>
            <time>
              {Math.floor(flow.callSecondsLeft / 60)}:
              {String(flow.callSecondsLeft % 60).padStart(2, '0')}
            </time>
          </div>
          {flow.callStatus === 'network' && (
            <p className="auth-ui-field-hint">
              Проверка продолжится автоматически, когда связь восстановится.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function AuthContent({ flow }: { flow: AuthFlow }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const profileError = flow.errorTarget === 'profile';
  const profileStage = profileError || flow.busy === 'profile';
  const confirming = flow.view === 'callme';
  const success = flow.view === 'success';
  const title = success
    ? 'Вы вошли.'
    : profileStage
      ? 'Почти готово.'
      : flow.view === 'callme'
        ? 'Позвоните для входа'
        : 'Вход в аккаунт';
  const description = success
    ? 'Сейчас откроем ваш личный кабинет.'
    : profileStage
      ? 'Номер подтверждён. Осталось загрузить профиль.'
      : confirming
        ? 'Номер для входа'
        : 'Заказы, адреса и коины — всё под рукой.';

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    let frame = 0;
    const resize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const node = contentRef.current;
        if (!node) return;
        node.style.setProperty('--auth-visible-height', `${viewport.height}px`);
        node.style.setProperty('--auth-visible-top', `${viewport.offsetTop}px`);
        node.dataset.keyboard = String(window.innerHeight - viewport.height > 150);
        const focused = document.activeElement;
        if (focused instanceof HTMLInputElement && node.contains(focused)) {
          focused.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });
    };
    resize();
    viewport.addEventListener('resize', resize);
    viewport.addEventListener('scroll', resize);
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener('resize', resize);
      viewport.removeEventListener('scroll', resize);
    };
  }, []);

  return (
    <Dialog.Content
      ref={contentRef}
      className="auth-ui-dialog"
      data-view={flow.view}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        openerRef.current =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
        if (window.matchMedia('(max-width: 767px)').matches) titleRef.current?.focus();
        else contentRef.current?.querySelector<HTMLInputElement>('#auth-phone')?.focus();
      }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        if (openerRef.current?.isConnected) openerRef.current.focus({ preventScroll: true });
      }}
    >
      <aside className="auth-ui-story" aria-hidden="true">
        <div className="auth-ui-story-copy">
          <p>
            Привет,
            <br />
            это Азиаточка.
          </p>
          <span>
            Сохраняйте адреса,
            <br />
            следите за заказами.
          </span>
        </div>
        <Image
          className="auth-ui-character"
          src={CHARACTER}
          alt=""
          width={760}
          height={950}
          sizes="380px"
          unoptimized
        />
        <div className="auth-ui-story-caption">
          <span className="auth-ui-caption-dot" />
          <span>Ваши заказы. Ваши бонусы.</span>
        </div>
      </aside>
      <div className="auth-ui-panel">
        <div className="auth-ui-topbar">
          <span>Личный кабинет</span>
          <button
            type="button"
            className="auth-ui-close"
            onClick={flow.close}
            aria-label="Закрыть окно входа"
          >
            <X size={23} />
          </button>
        </div>
        <div className="auth-ui-scroll">
          <div className="auth-ui-heading">
            {confirming && !success && (
              <button type="button" className="auth-ui-back" onClick={flow.back}>
                <ArrowLeft size={17} />
                Изменить номер
              </button>
            )}
            {!success && (
              <div className="auth-ui-progress" aria-label={`Шаг ${confirming ? 2 : 1} из 2`}>
                <span className="is-done" />
                <span className={confirming ? 'is-done' : ''} />
                <small>{confirming ? '02' : '01'} / 02</small>
              </div>
            )}
            {success && (
              <div className="auth-ui-success-mark">
                <CheckIcon size={36} weight="bold" />
              </div>
            )}
            <Dialog.Title ref={titleRef} tabIndex={-1}>
              {title}
            </Dialog.Title>
            <Dialog.Description>{description}</Dialog.Description>
            {confirming && !profileStage && (
              <p className="auth-ui-verified-phone">{formatPhone(flow.phone)}</p>
            )}
            {flow.view === 'phone' && (
              <p className="auth-ui-new-account">Впервые у нас? Аккаунт создастся при входе.</p>
            )}
          </div>
          {success ? (
            <div className="auth-ui-success-note" role="status">
              <CheckCircleIcon size={20} />
              Всё готово. Рады видеть вас!
            </div>
          ) : profileStage ? (
            <div className="auth-ui-profile-retry">
              {flow.error && <AuthNotice>{flow.error}</AuthNotice>}
              <AuthPrimary onClick={flow.retryProfile} busy={flow.busy === 'profile'}>
                Открыть аккаунт
              </AuthPrimary>
            </div>
          ) : flow.view === 'callme' ? (
            <CallMeStep flow={flow} />
          ) : (
            <PhoneStep flow={flow} />
          )}
        </div>
        <footer className="auth-ui-support">
          <PhoneIcon size={17} />
          <span>Поможем со входом</span>
          <a href="tel:+78633335050">8 (863) 333-50-50</a>
        </footer>
      </div>
    </Dialog.Content>
  );
}

export function AuthModal() {
  const flow = useAuthFlow();
  return (
    <Dialog.Root
      open={flow.isOpen}
      onOpenChange={(open) => {
        if (!open) flow.close();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="auth-ui-overlay" />
        <AuthContent flow={flow} />
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default AuthModal;
